/**
 * ProductDetails Component
 *
 * This component renders the detailed view of a product, including its price, description,
 * reviews, and options to add it to the shopping cart.
 *
 * Props:
 * - averageRating: The average rating of the product.
 * - description: The detailed description of the product.
 * - price: The price of the product.
 * - props: Additional properties related to the product.
 * - reviews: An array of reviews for the product.
 * - size: The size of the product.
 * - sku: The stock keeping unit identifier for the product.
 * - title: The title or name of the product.
 * - url: The URL of the product's image or page.
 *
 * State:
 * - alert: Manages the state of the alert component, including its type and message.
 *
 * Functions:
 * - showToast: Sets the alert state with the provided type, title, and quantity, triggering
 *   the display of an alert with a corresponding message.
 *
 * Usage:
 * The component uses `AddToCartForm` to allow users to add the product to the cart and displays
 * an alert message when a product is successfully added.
 */

'use client';

// Package Imports
import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useMediaQuery } from 'react-responsive';
import { useSession } from 'next-auth/react';
import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons';

// Type Imports
import { Product as ProductType } from '@/lib/types/products';

// Function Imports
import { checkProductStock, ProductStockState } from '@/lib/functions/checkProductStock';
import { defaultStoreNumber } from '@/lib/constants/store';
import { isPriceValid } from '@/lib/functions/isPriceValid';
import { isProductShippable } from '@/lib/functions/isProductShippable';

// Style Imports
import styles from './productDetails.module.scss';

// Type Imports
import { SyliusAttribute } from '@/lib/types/products';

// Component Imports
import AddToCartMobile from '@/components/product/addToCartMobile';
import AddToCartForm from '@/components/product/addToCartForm';
import ProductPrice from '@/components/product/productPrice';
import Toast from '@/components/toast';
import StylizedButton from '@/components/stylizedButton';
import { ProductRatings } from '@/components/product/productRatings';
import { ProductStock } from '@/components/product/productStock';
import ProductShippingDebug from '@/components/product/productShippingDebug';

// Constants Imports
import { strings } from '@/lib/data/site/strings';
import { brandSettings } from '@/lib/data/site/brandSettings';

// Define Interfaces
interface ToastState {
  id: string;
  type: 'error' | 'success' | 'notice';
  message: string;
}

interface ProductDetailsProps extends ProductType {
  attributes?: SyliusAttribute[];
  autoHidePreviousToasts?: boolean;
  slug: string;
  reviewsCount: number;
}

// Define Component
export const ProductDetails: React.FC<ProductDetailsProps> = ({
  attributes = [],
  averageRating,
  description = "",
  variants,
  sku,
  title,
  url,
  autoHidePreviousToasts = false,
  reviewsCount = 0,
  slug,
  shippingCategory: productShippingCategory,
  shippingDimensions,
}) => {
  const { data: session } = useSession();
  const isLoggedIn = session && session.user;
  const isTablet = useMediaQuery({ query: '(max-width: 991px)' });

  const [alerts, setToasts] = useState<ToastState[]>([]);
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [submitReviewSuccess, setSubmitReviewSuccess] = useState(false);
  const [stockState, setStockState] = useState<ProductStockState | null>(null);

  // Find the first variant that matches the store number in its code
  const storeNumber = defaultStoreNumber;
  const matchedVariant =
    variants?.[storeNumber] ??
    (variants ? Object.values(variants)[0] : undefined);

  const displayAttributes: { label: string; code: string }[] = [
    { label: 'Size', code: 'size' },
    { label: 'Brand', code: 'brand' },
    { label: 'Origin', code: 'origin' },
    { label: 'Region', code: 'region' },
  ];

  const {
    casePrice,
    casePriceKeyclubDiscount,
    casePricePromoDiscount,
    caseQuantity,
    code,
    unitPrice,
    unitPriceKeyclubDiscount,
    unitPricePromoDiscount,
    shippingCategory: variantShippingCategory,
  } = matchedVariant ?? {};

  const shippingCategory = variantShippingCategory ?? productShippingCategory ?? null;
  const isShippable = isProductShippable(shippingCategory, shippingDimensions);

  const variantByNumberKey = variants?.[storeNumber];
  const variantByStringKey = variants?.[String(storeNumber)];
  const fallbackVariant = variants ? Object.values(variants)[0] : undefined;

  // Find the variant that matches the store
  useEffect(() => {
    if (variants && storeNumber !== null) {
      if (matchedVariant && isPriceValid(unitPrice ?? undefined)) {
        setActiveCode(code || null);
      } else {
        setActiveCode(null);
      }
    }
  }, [storeNumber, variants, code, matchedVariant, unitPrice]);

  const showToast = (type: 'error' | 'success' | 'notice', message: string) => {
    const newToast = {
      id: uuidv4(),
      type,
      message,
    };
    setToasts((prevToasts) => [...prevToasts, newToast]);

    if (autoHidePreviousToasts) {
      setTimeout(() => {
        setToasts((prevToasts) => prevToasts.slice(1));
      }, 5000);
    }
  };

  const handleDismiss = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter(alert => alert.id !== id));
  };

  useEffect(() => {
    const fetchStock = async () => {
      if (code && storeNumber !== null) {
        const stock = await checkProductStock(code, caseQuantity ?? 10);
        setStockState(stock);
      }
    };

    void fetchStock();
  }, [code, storeNumber, caseQuantity]);

  const isAddToCartDisabled =
    stockState == null || !stockState.inStock || !isShippable;

  const shippingDebug = {
    defaultStoreNumber: storeNumber,
    defaultStoreIdEnv: process.env.NEXT_PUBLIC_DEFAULT_STORE_ID ?? null,
    sku,
    variantLookup: {
      keysInVariantsObject: variants ? Object.keys(variants) : [],
      matchedByNumberKey: variantByNumberKey?.code ?? null,
      matchedByStringKey: variantByStringKey?.code ?? null,
      fallbackFirstVariantCode: fallbackVariant?.code ?? null,
      resolvedVariantCode: matchedVariant?.code ?? null,
    },
    variantsByStore: variants
      ? Object.entries(variants).map(([storeKey, variant]) => ({
          storeKey,
          code: variant.code ?? null,
          shippingCategory: variant.shippingCategory ?? null,
          inStock: variant.inStock ?? null,
          unitPrice: variant.unitPrice ?? null,
        }))
      : [],
    resolvedVariant: matchedVariant ?? null,
    shippingCategory: shippingCategory ?? null,
    shippingDimensions: shippingDimensions ?? null,
    isShippable,
    addToCart: {
      activeCode,
      unitPrice: unitPrice ?? null,
      unitPriceValid: isPriceValid(unitPrice ?? undefined),
      stockState,
      showAddToCartBlock: Boolean(unitPrice && isPriceValid(unitPrice) && stockState?.available),
      disabled: isAddToCartDisabled,
    },
    note: 'Non-shippable items include restricted shipping and oversized products.',
  };

  return (
    <div className={styles.details}>
      <ProductShippingDebug label="Product shipping debug (dev or ?debugProduct=1)" data={shippingDebug} />
      {alerts.map((alert) => (
        <Toast
          key={alert.id}
          id={alert.id}
          type={alert.type}
          message={alert.message}
          onDismiss={() => handleDismiss(alert.id)}
          dismissible
          fadeOutTime={5000}
        />
      ))}
      {unitPrice && isPriceValid(unitPrice) && stockState?.available && (
        <div className={styles.addToCartWrapper}>
          <div className={classNames(styles.addToCart)}>
            <ProductPrice
              unitPrice={unitPrice}
              unitPricePromoDiscount={unitPricePromoDiscount || 0}
              unitPriceKeyclubDiscount={unitPriceKeyclubDiscount || 0}
              casePrice={casePrice || unitPrice}
              caseQuantity={caseQuantity || 1}
              casePricePromoDiscount={casePricePromoDiscount || 0}
              casePriceKeyclubDiscount={casePriceKeyclubDiscount || 0}
              isKeyclubMember={isLoggedIn}
              isCasePrice={false}
            />
            {/* Standard Price - Add To Cart */}
            { isTablet ? (
              <AddToCartMobile
                code={activeCode ?? ''}
                title={title}
                showToast={showToast}
                disabled={isAddToCartDisabled}
              />
            ) : (
              <AddToCartForm
                title={title}
                sku={sku}
                code={activeCode}
                url={url}
                showToast={showToast}
                disabled={isAddToCartDisabled}
              />
            )}
          </div>
          {typeof caseQuantity === 'number' && caseQuantity > 0 &&
            stockState?.quantityAvailable != null &&
            stockState.quantityAvailable >= caseQuantity && (
            <div className={styles.divider} />
          )}

          {/* Case - Add To Cart */}
          <div
            className={classNames(
              styles.addToCart,
              styles.addToCartCases
            )}
          >
            {typeof caseQuantity === 'number' && caseQuantity > 0 &&
              stockState?.quantityAvailable != null &&
              stockState.quantityAvailable >= caseQuantity && (
              <>
                <ProductPrice
                  unitPrice={unitPrice}
                  unitPricePromoDiscount={unitPricePromoDiscount || 0}
                  unitPriceKeyclubDiscount={unitPriceKeyclubDiscount || 0}
                  casePrice={casePrice || unitPrice}
                  caseQuantity={caseQuantity || 1}
                  casePricePromoDiscount={casePricePromoDiscount || 0}
                  casePriceKeyclubDiscount={casePriceKeyclubDiscount || 0}
                  isKeyclubMember={isLoggedIn}
                  isCasePrice={true}
                />
                { caseQuantity && (
                  <>
                    {isTablet ? (
                      <AddToCartMobile
                        code={activeCode ?? ''}
                        caseQuantity={caseQuantity}
                        title={title}
                        showToast={showToast}
                        disabled={isAddToCartDisabled}
                        label="ADD CASE TO CART" 
                      />
                    ) : (
                      <AddToCartForm
                        code={activeCode}
                        caseQuantity={caseQuantity}
                        title={title}
                        label="ADD CASE TO CART"
                        url={url}
                        showToast={showToast}
                        disabled={isAddToCartDisabled}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
          <div className={styles.additionalFeatures}>
            {(unitPriceKeyclubDiscount || casePriceKeyclubDiscount) ?
              (
                isLoggedIn ? (
                  <div className={styles.keyClubExclusiveLabel}>
                    <Image
                      src={strings.loyaltyProgram.icon} 
                      width={22}
                      height={32}
                      alt=""
                      className="exclusiveIcon"
                    />
                    <span className={styles.exclusiveText}>{strings.loyaltyProgram.name} Exclusive</span>
                  </div>
                ) : (
                  <Link
                    className={
                      process.env.NEXT_PUBLIC_SYLIUS_CHANNEL_CODE === 'SPECS' 
                        ? styles.keyclubJoin : styles.keyclubJoinFeldmans
                    }
                    href={`/${strings.loyaltyProgram.url}/${strings.loyaltyProgram.joinUrl}`}
                  >
                    { process.env.NEXT_PUBLIC_SYLIUS_CHANNEL_CODE === 'SPECS' ? (
                      <>
                        <Image
                          src={strings.loyaltyProgram.icon}
                          width={22}
                          height={32}
                          alt=""
                          className="exclusiveIcon"
                        />
                        <span className={styles.joinText}>
                          Join {strings.brandNameShort} {strings.loyaltyProgram.name}
                        </span>
                      </>
                    ) : (
                      <span className={styles.joinTextFeldmans}>
                        <Image
                          src={'/images/brands/feldmans/loyalty-program/rewards-join.svg'}
                          width={227}
                          height={44}
                          alt="Join Feldmans Rewards"
                          className="exclusiveIcon"
                        />
                      </span>
                    )}
                  </Link>
                )
              ) : ('')
            }
            {process.env.NEXT_PUBLIC_SHIPPING_ENABLED === 'true' && (
              <div className={styles.shipToMyDoor}>
                <StylizedButton className={styles.shipButton}>Ship to my door</StylizedButton>
              </div>
            )}
          </div>
        </div>
      )}
      {!isShippable && (
        <div className={styles.nonShippableMessage}>
          <FontAwesomeIcon icon={faPhone} className={styles.noShipIcon} aria-hidden />
          <span className={styles.nonShippableText}>
            This item requires special shipping. Please call 713-898-3103 to order and for shipping estimates.
          </span>
        </div>
      )}
      { stockState && stockState.available && (
        <div className={styles.stockStateWrapper}>
          <ProductStock
            stockState={{ available: true, inStock: stockState.inStock, lowStock: stockState.lowStock, message: stockState.message }} 
            productSku={sku}
          />
        </div>
      )}
      <div className={styles.props}>
        <div className={styles.meta}>
          {displayAttributes.map(({ label, code }) => {
            const attr = attributes?.find((attr: SyliusAttribute) => attr.code?.toLowerCase() === code);
            return attr?.value ? (
              <p key={`attr-${code}`} className={code === 'size' ? styles.size : styles.extras}>
                {label}: {attr.value}
              </p>
            ) : null;
          })}

          {sku && <p className={styles.sku}>SKU: {sku}</p>}
          {activeCode && <p className={styles.code}>VARIANT: {activeCode}</p>}
        </div>
        { brandSettings?.hasProductRatings && sku && (
          <div className={styles.reviews}>
            <ProductRatings
              averageRating={averageRating}
              reviewsCount={reviewsCount}
              slug={slug}
              sku={sku}
              submitReviewSuccess={submitReviewSuccess}
              setSubmitReviewSuccess={setSubmitReviewSuccess}
            />
          </div>
        )}
      </div>

      {/* Show the review submission success toast if necessary */}
      {submitReviewSuccess && (
        <Toast
          id="reviewSucessToast"
          type="success"
          label={false}
          message="Review submitted! It can take 24 hours or more for your review to appear."
        />
      )}

      <div className={styles.description}>
        {description &&
          <p>{parse(description)}</p>
        }
        <p className={styles.discount}>
          Receive an additional 5% discount in-store for cash payment.
          Prices and vintages subject to change. Prices shown are for this
          location only and can vary by location. Photos are for display
          purposes only and may not reflect the actual product.
        </p>
      </div>
    </div>
  );
};
